import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  collection,
} from "firebase/firestore";
import { db } from "../firebase";
import { US_STATES } from "../utils/states";
import toast from "react-hot-toast";

function OrganizationEntry() {
  const { orgId } = useParams();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isNewMode, setIsNewMode] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [adminSignupCode, setAdminSignupCode] = useState("");
  const [authenticationCode, setAuthenticationCode] = useState("");
  const [authenticationLabel, setAuthenticationLabel] =
    useState("Library Card");
  const [city, setCity] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [country, setCountry] = useState("");
  const [location, setLocation] = useState("");
  const [state, setState] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [zipCode, setZipCode] = useState("");

  useEffect(() => {
    const fetchOrganization = async () => {
      if (orgId === "new" || orgId === undefined) {
        setIsNewMode(true);
        setLoading(false);
        return;
      }

      try {
        const orgRef = doc(db, "organizations", orgId);

        const orgSnap = await getDoc(orgRef);

        if (orgSnap.exists()) {
          const orgData = orgSnap.data();

          setOrganization({ id: orgSnap.id, ...orgData });

          // Set form values
          setName(orgData.name || "");
          setActive(orgData.active !== false);
          setAdminSignupCode(orgData.admin_signup_code || "");
          setAuthenticationCode(orgData.authentication_code || "");
          setAuthenticationLabel(orgData.authentication_label || "");
          setCity(orgData.city || "");
          setContactEmail(orgData.contact_email || "");
          setCountry(orgData.country || "");
          setLocation(orgData.location || "");
          setState(orgData.state || "");
          setStreetAddress(orgData.street_address || "");
          setZipCode(orgData.zip_code || "");
        } else {
          toast.error("Organization not found");
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching organization:", error);
        toast.error("Error loading organization");
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [orgId]);

  const validateForm = () => {
    if (!name.trim()) {
      toast.error("Organization name is required");
      return false;
    }
    if (!contactEmail.trim()) {
      toast.error("Contact email is required");
      return false;
    }
    if (!authenticationLabel.trim()) {
      toast.error("Authentication label is required");
      return false;
    }
    return true;
  };

  const generateAdminSignupCode = () => {
    // Generate a random 8-character code
    return Math.random().toString(36).substring(2, 10).toUpperCase();
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      if (isNewMode) {
        // Create new organization
        const newOrgData = {
          name: name.trim(),
          active,
          admin_signup_code: generateAdminSignupCode(),
          authentication_code: authenticationCode.trim(),
          authentication_label: authenticationLabel.trim(),
          city: city.trim(),
          contact_email: contactEmail.trim(),
          country: country.trim(),
          location: location.trim(),
          state: state.trim(),
          street_address: streetAddress.trim(),
          zip_code: zipCode.trim(),
          created_at: new Date(),
          updated_at: new Date(),
        };

        await addDoc(collection(db, "organizations"), newOrgData);

        toast.success("Organization created successfully!");
        navigate("/admin/organizations");
      } else {
        // Update existing organization
        const orgRef = doc(db, "organizations", orgId);
        await updateDoc(orgRef, {
          name: name.trim(),
          active,
          admin_signup_code: adminSignupCode,
          authentication_code: authenticationCode.trim(),
          authentication_label: authenticationLabel.trim(),
          city: city.trim(),
          contact_email: contactEmail.trim(),
          country: country.trim(),
          location: location.trim(),
          state: state.trim(),
          street_address: streetAddress.trim(),
          zip_code: zipCode.trim(),
          updated_at: new Date(),
        });

        toast.success("Organization updated successfully!");
        navigate("/admin/organizations");
      }
    } catch (error) {
      console.error("Error saving organization:", error);

      // More specific error messages
      if (error.code === "permission-denied") {
        toast.error("Permission denied. Please check your admin privileges.");
      } else if (error.code === "unavailable") {
        toast.error("Service temporarily unavailable. Please try again.");
      } else if (error.code === "already-exists") {
        toast.error("An organization with this name already exists.");
      } else {
        toast.error(`Error saving organization: ${error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const confirmed = window.confirm(
      "Are you sure you want to cancel? Any unsaved changes will be lost."
    );
    if (confirmed) {
      navigate("/admin/organizations");
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${name}"? This action cannot be undone.`
    );
    if (confirmed) {
      try {
        const orgRef = doc(db, "organizations", orgId);
        await deleteDoc(orgRef);

        toast.success("Organization deleted successfully!");
        navigate("/admin/organizations");
      } catch (error) {
        console.error("Error deleting organization:", error);
        toast.error("Error deleting organization");
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Link copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy to clipboard");
      });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading organization...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!organization && !isNewMode) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              Organization not found
            </div>
          </div>
        </div>
      </div>
    );
  }

  const userInviteUrl = isNewMode
    ? "Will be available after creation"
    : `https://app.kuzkohistory.com/sign-up?id=${orgId}`;

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary-600">
              {isNewMode ? "New Organization" : "Edit Organization"}
            </h1>
            <p className="text-gray-600 mt-2">
              {isNewMode
                ? "Create a new organization"
                : organization?.name || ""}
            </p>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter organization name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Active
                  </label>
                  <select
                    value={active.toString()}
                    onChange={(e) => setActive(e.target.value === "true")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Signup Code
                  </label>
                  <input
                    type="text"
                    value={adminSignupCode}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter contact email"
                  />
                </div>
              </div>
            </div>

            {/* Authentication */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Authentication
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Authentication Code
                  </label>
                  <input
                    type="text"
                    value={authenticationCode}
                    onChange={(e) => setAuthenticationCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter authentication code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Authentication Label <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={authenticationLabel}
                    onChange={(e) => setAuthenticationLabel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter authentication label"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Location
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={streetAddress}
                    onChange={(e) => setStreetAddress(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <select
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select a state...</option>
                    {US_STATES.map((stateOption) => (
                      <option key={stateOption.code} value={stateOption.code}>
                        {stateOption.code} - {stateOption.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Invite URLs */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Invite URLs
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Invite URL
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      value={userInviteUrl}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50"
                    />
                    <button
                      onClick={() => copyToClipboard(userInviteUrl)}
                      disabled={isNewMode}
                      className={`px-4 py-2 rounded-r-md transition-colors ${
                        isNewMode
                          ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                          : "bg-primary-600 text-white hover:bg-primary-700"
                      }`}
                    >
                      Copy
                    </button>
                  </div>
                </div>
                {/*<div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Invite URL
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      value={adminInviteUrl}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50"
                    />
                    <button
                      onClick={() => copyToClipboard(adminInviteUrl)}
                      disabled={isNewMode}
                      className={`px-4 py-2 rounded-r-md transition-colors ${
                        isNewMode
                          ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                          : "bg-primary-700 text-white hover:bg-primary-800"
                      }`}
                    >
                      Copy
                    </button>
                  </div>
                </div>*/}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-start space-x-4 mt-8">
            <button
              onClick={handleSave}
              disabled={saving}
              className={`px-6 py-2 rounded-md transition-colors ${
                saving
                  ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                  : "bg-primary-600 text-white hover:bg-primary-700"
              }`}
            >
              {saving ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isNewMode ? "Creating..." : "Saving..."}
                </div>
              ) : isNewMode ? (
                "Create"
              ) : (
                "Save"
              )}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className={`px-6 py-2 rounded-md transition-colors ${
                saving
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-gray-500 text-white hover:bg-gray-600"
              }`}
            >
              Cancel
            </button>
            {!isNewMode && (
              <button
                onClick={handleDelete}
                disabled={saving}
                className={`px-6 py-2 rounded-md transition-colors ${
                  saving
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700"
                }`}
              >
                Delete Organization
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrganizationEntry;
